import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Crown, Trophy, Puzzle, Star, Send, CheckCircle2, Zap, Megaphone, Settings } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/game/profile").then(({ data }) => setData(data)).catch(() => {});
  }, [user, authLoading, navigate]);

  if (!data) return <Layout><div className="py-32 text-center font-mono-caps text-xs text-primary animate-pulse">Loading…</div></Layout>;

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  const stats = [
    { icon: Trophy, label: "Total Score", value: data.stats.total_score },
    { icon: Puzzle, label: "Solved", value: data.stats.total_solved },
    { icon: Star, label: "Badges", value: data.stats.achievements },
    { icon: Flame, label: "Games", value: data.stats.games_played },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-center gap-5 mb-10">
          {data.user.picture ? (
            <img src={data.user.picture} alt="" className="w-16 h-16 border-2 border-primary object-cover" />
          ) : (
            <div className="w-16 h-16 bg-primary text-primary-foreground font-display text-3xl flex items-center justify-center">{(data.user.name || "?")[0]}</div>
          )}
          <div>
            <h1 className="font-display text-4xl">{data.user.name}</h1>
            <p className="font-mono-caps text-[10px] text-muted-foreground flex items-center gap-2">
              {data.user.email}
              {data.user.is_premium && <span className="text-primary flex items-center gap-1"><Crown size={12} /> Premium</span>}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {stats.map((s) => (
            <div key={s.label} className="border border-white/15 bg-card p-5" data-testid={`stat-${s.label}`}>
              <s.icon className="text-primary mb-3" size={20} />
              <div className="font-display text-3xl">{s.value}</div>
              <div className="font-mono-caps text-[9px] text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-3xl mb-5">STREAKS BY ZONE</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {["casual", "golden_era", "new_and_now"].map((z) => {
            const s = data.streaks[z] || { current: 0, longest: 0 };
            return (
              <div key={z} className="border border-white/15 bg-card p-5" data-testid={`streak-${z}`}>
                <p className="font-mono-caps text-[10px] text-muted-foreground mb-3">{data.zone_names[z]}</p>
                <div className="flex items-center gap-2"><Flame className="text-primary" size={26} /><span className="font-display text-4xl">{s.current}</span></div>
                <p className="font-mono-caps text-[9px] text-muted-foreground mt-2">Best: {s.longest} days</p>
              </div>
            );
          })}
        </div>

        <h2 className="font-display text-3xl mb-5">COMMUNITY CONTRIBUTIONS</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12" data-testid="contributions">
          {[
            { icon: Send, label: "Submitted", value: data.contributions?.submitted ?? 0 },
            { icon: CheckCircle2, label: "Approved", value: data.contributions?.approved ?? 0 },
            { icon: Zap, label: "Used in Puzzles", value: data.contributions?.used ?? 0 },
            { icon: Megaphone, label: "Shout-Outs", value: data.contributions?.shoutouts_received ?? 0 },
          ].map((s) => (
            <div key={s.label} className="border border-white/15 bg-card p-5" data-testid={`contrib-${s.label}`}>
              <s.icon className="text-secondary mb-3" size={20} />
              <div className="font-display text-3xl">{s.value}</div>
              <div className="font-mono-caps text-[9px] text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {data.user.is_premium ? (
          <div className="border-2 border-primary bg-primary/5 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="manage-subscription-block">
            <div className="flex items-center gap-3"><Crown className="text-primary" size={26} />
              <div><div className="font-display text-2xl">Premium Member</div>
                <div className="text-sm text-muted-foreground">Manage or cancel your subscription securely on Stripe.</div></div>
            </div>
            <button onClick={openPortal} data-testid="manage-subscription-btn" className="flex items-center gap-2 bg-foreground text-background font-mono-caps text-xs px-5 py-3 hover-lift border border-foreground">
              <Settings size={15} /> Manage Subscription
            </button>
          </div>
        ) : (
          <Link to="/premium" className="block border-2 border-primary bg-primary/5 p-6 hover-lift" data-testid="profile-upgrade">
            <div className="flex items-center gap-3"><Crown className="text-primary" size={26} />
              <div><div className="font-display text-2xl">Upgrade to Premium</div>
                <div className="text-sm text-muted-foreground">Unlock all zones, remove ads & get enhanced hints.</div></div>
            </div>
          </Link>
        )}
      </div>
    </Layout>
  );
}
